import React from 'react'
import { Link } from 'react-router'

import { REFERRALS_TAB } from '../../../../lib/constants'
import {
  buildDebugTooltip,
  truncateText,
} from '../../../../lib/helpers/helperFunctions'
import { ROUTES } from '../../../../lib/routes'
import { shareableLinkRowId } from '../../../../pages/emailAndCalendarIntegration/components/ShareableLinks/helpers'
import { REFERRAL_LINK_PAGE_PARAMS } from '../../../../pages/referralLink/constants'
import { referralsRoute } from '../../../../pages/referrals/helpers'
import CompanyLogoOrNameTableCell from '../../CompanyLogoOrNameTableCell'
import CopyButton from '../../CopyButton'
import ExternalLink from '../../ExternalLink'
import CopyLink from '../../PanelInviteEmailTemplate/CopyLink'
import PersistentOverlayTrigger from '../../PersistentOverlayTrigger'
import PlainButton from '../../PlainButton'
import { isReferralLinkCollectionItem } from '../helpers'
import { useIsViewingAsFromCompany } from '../hooks/useIsViewingAsFromCompany'
import { ReferralLinkTableRow } from '../types'
import DeleteLinkButton from './DeleteLinkButton'
import DuplicateButton from './DuplicateButton'
import IntroduceTo from './IntroduceTo'

interface Props {
  referralLink: ReferralLinkTableRow
  onEditClick: (referralLink: Props['referralLink']) => void
}

const LINK_CODE_TRUNCATE_THRESHOLD = 100

const ReferralLinksTableRow: React.FC<Props> = ({
  referralLink,
  onEditClick,
}) => {
  const isViewingAsFromCompany = useIsViewingAsFromCompany()
  const numReferrals = referralLink.numReferrals

  const otherCompany = isViewingAsFromCompany
    ? referralLink.toCompany
    : referralLink.fromCompany

  const referralLinkPath = ROUTES.referralLink.replace(
    `:${REFERRAL_LINK_PAGE_PARAMS.code}`,
    referralLink.currentCompanyCode,
  )

  const referralLinkUrl = window.location.origin + referralLinkPath
  const linkCode = referralLink.currentCompanyCode

  return (
    <tr
      id={shareableLinkRowId(referralLink.id)}
      key={referralLink['@id']}
      title={buildDebugTooltip({
        ID: referralLink.id,
        createdAt: referralLink.createdAt,
        currentCompanyCode: referralLink.currentCompanyCode,
        wasDuplicated: referralLink.wasDuplicated,
      })}
    >
      {/* Introduce to / Partner company */}
      <td>
        {isReferralLinkCollectionItem(referralLink) ? (
          <IntroduceTo referralLink={referralLink} />
        ) : (
          <CompanyLogoOrNameTableCell company={otherCompany} />
        )}
      </td>

      {/* Referrals booked */}
      <td>
        {numReferrals && numReferrals > 0 ? (
          <Link
            className="py-2 text-decoration-underline"
            to={referralsRoute({
              tab: isViewingAsFromCompany
                ? REFERRALS_TAB.sent
                : REFERRALS_TAB.received,
              referralLinkCode: referralLink.currentCompanyCode,
            })}
          >
            {numReferrals}
          </Link>
        ) : (
          <span className="d-inline-block py-2">{numReferrals}</span>
        )}
      </td>

      {/* Link / Link code */}
      <td>
        <div className="d-flex flex-column column-gap-2">
          <PersistentOverlayTrigger
            id={`referralLink-code-${referralLink.currentCompanyCode}`}
            tooltip={
              linkCode.length > LINK_CODE_TRUNCATE_THRESHOLD ? linkCode : null
            }
          >
            {truncateText(linkCode, {
              maxLength: LINK_CODE_TRUNCATE_THRESHOLD,
            })}
          </PersistentOverlayTrigger>

          <div className="d-flex gap-2">
            <CopyButton textToCopy={linkCode}>
              {({ copyText, justCopied }) =>
                justCopied ? (
                  <span className="text-success">Code copied!</span>
                ) : (
                  <PlainButton className="hover-underline" onClick={copyText}>
                    Copy code
                  </PlainButton>
                )
              }
            </CopyButton>

            {referralLink.canEditCode && (
              <>
                <div>|</div>

                <PlainButton
                  className="hover-underline"
                  onClick={() => onEditClick(referralLink)}
                >
                  Edit code
                </PlainButton>
              </>
            )}
          </div>
        </div>
      </td>

      {/* Link actions */}
      <td>
        <div className="d-flex flex-column gap-2">
          <ExternalLink
            iconPlacement="start"
            className="btn btn-sm btn-outline-secondary"
            href={referralLinkUrl}
          >
            Preview
          </ExternalLink>

          <CopyLink
            textToCopy={referralLinkUrl}
            buttonText="Copy link"
            variant="outline-success"
            className="btn-fluid btn-sm"
            style={{ minWidth: '95px' }}
          />

          {referralLink.isRoundRobinLink && (
            <DuplicateButton
              referralLinkCode={referralLink.currentCompanyCode}
            />
          )}

          {referralLink.canBeDeleted && (
            <DeleteLinkButton
              referralLinkCode={referralLink.currentCompanyCode}
            />
          )}
        </div>
      </td>
    </tr>
  )
}

export default ReferralLinksTableRow
